import { useCallback } from 'react';
import {
  generateKeyPair,
  generateSalt,
  deriveKEK,
  encryptPrivateKey,
  decryptPrivateKey,
  decodeBase64,
  encodeBase64,
} from '@clinikchat/shared';
import { useCryptoStore } from '../stores/cryptoStore';

import api from '../lib/api';
import type { KeyBundle } from '@clinikchat/shared';

export function useKeyInit() {
  const setSecretKey = useCryptoStore((s) => s.setSecretKey);
  const setPublicKey = useCryptoStore((s) => s.setPublicKey);
  const initKeys = useCallback(
    async (password: string): Promise<void> => {
      // Check if server already has a key bundle for this user
      const { data: bundleRes } = await api.get<{ success: true; data: KeyBundle | null }>(
        '/api/v1/auth/keys',
      );
      const bundle = bundleRes.data;

      if (bundle) {
        // Existing key bundle — derive KEK and decrypt private key
        const kek = await deriveKEK(password, bundle.keyDerivationSalt);
        const secretKeyBytes = decryptPrivateKey(bundle.encryptedPrivateKey, kek);
        if (!secretKeyBytes) {
          throw new Error('Failed to decrypt private key — password may be incorrect');
        }
        setSecretKey(encodeBase64(secretKeyBytes));
        setPublicKey(bundle.publicKey);
      } else {
        // First login — generate new key pair
        const { publicKey, secretKey } = generateKeyPair();
        const salt = generateSalt();
        const kek = await deriveKEK(password, salt);
        const encryptedPrivateKey = encryptPrivateKey(decodeBase64(secretKey), kek);

        await api.post('/api/v1/auth/keys', {
          publicKey,
          encryptedPrivateKey,
          keyDerivationSalt: salt,
        } satisfies KeyBundle);

        setSecretKey(secretKey);
        setPublicKey(publicKey);
      }
    },
    [setSecretKey, setPublicKey],
  );

  return { initKeys };
}
